import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import './operations-report.css';

const WEEKDAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MONTH_FORMAT = new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const MONEY_FORMAT = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 });

function money(cents) {
  const value = Number.isSafeInteger(cents) ? cents : 0;
  return MONEY_FORMAT.format(value / 100);
}

function monthLabel(value) {
  const [year, month] = String(value || '').split('-').map(Number);
  if (!year || !month) return value || '—';
  return MONTH_FORMAT.format(new Date(Date.UTC(year, month - 1, 1)));
}

function dateLabel(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value || '';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, day)));
}

function metricValue(row, field) {
  if (!row) return '—';
  if (field === 'collectedCents' || field === 'overdueBalanceCents') return money(row[field]);
  if (field === 'deactivations' && row[field] == null) return 'Sin historial';
  return Number(row[field] || 0).toLocaleString('es-AR');
}

function metricCard(title, value, detail, variant = '') {
  return (
    <article className={`gym-operations__metric${variant ? ` gym-operations__metric--${variant}` : ''}`} key={title}>
      <h2>{title}</h2>
      <p className="gym-operations__metric-value">{value}</p>
      {detail && <p className="gym-operations__metric-detail">{detail}</p>}
    </article>
  );
}

/** Operations summary follows server-owned month ranges and pre-aggregated values. */
export function OperationsReportPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [filters, setFilters] = useState({ from: '', to: '' });
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [exporting, setExporting] = useState('');
  const [exportError, setExportError] = useState('');
  const lastParams = useRef({});
  const requestId = useRef(0);

  const load = useCallback((params = {}) => {
    const id = ++requestId.current;
    lastParams.current = params;
    setStatus('loading');
    setMessage('');
    setRangeError('');
    api.getOperationsReport(params)
      .then((result) => {
        if (requestId.current !== id) return;
        setReport(result || null);
        setFilters({ from: (result && result.from) || params.from || '', to: (result && result.to) || params.to || '' });
        setStatus('ready');
      })
      .catch((error) => {
        if (requestId.current !== id) return;
        const outcome = handleApiError(error, { clearBusinessData: () => setReport(null) });
        if (outcome.handled) return;
        setReport(null);
        setMessage(errorMessageFor(error));
        setStatus('error');
      });
  }, [api, handleApiError]);

  useEffect(() => {
    load({});
    return () => { requestId.current += 1; };
  }, [load]);

  function changeFilter(key, value) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setRangeError('');
    if (!next.from && !next.to) {
      load({});
      return;
    }
    if (!next.from || !next.to) {
      setRangeError('Completá ambos meses para aplicar el período.');
      return;
    }
    if (next.from > next.to) {
      setRangeError('El mes inicial debe ser anterior o igual al mes final.');
      return;
    }
    load({ from: next.from, to: next.to });
  }

  async function download(format) {
    if (!report || exporting) return;
    setExporting(format);
    setExportError('');
    try {
      await api.downloadOperationsReport({ from: report.from, to: report.to, format });
    } catch (error) {
      const outcome = handleApiError(error);
      if (!outcome.handled) setExportError(errorMessageFor(error));
    } finally {
      setExporting('');
    }
  }

  const monthly = (report && Array.isArray(report.monthly)) ? report.monthly : [];
  const latest = monthly.length ? monthly[monthly.length - 1] : null;
  const maxCollections = Math.max(1, ...monthly.map((row) => Number(row.collectedCents) || 0));
  const attendance = (report && report.attendance) || { totalCheckins: 0, byWeekday: [], byHour: [] };
  const maxWeekdayCheckins = Math.max(1, ...(attendance.byWeekday || []).map((item) => Number(item.checkins) || 0));
  const topMembers = (report && Array.isArray(report.topMembers)) ? report.topMembers : [];

  return (
    <section className="gym-page gym-operations" aria-labelledby="gym-page-title">
      <header className="gym-operations__header">
        <div>
          <h1 id="gym-page-title" className="gym-page__title">Reportes operativos</h1>
          <p className="gym-operations__intro">Recaudación, estado de socios y ritmo de asistencia.</p>
        </div>
        <div className="gym-operations__actions" aria-label="Descargar reporte">
          <button type="button" className="gym-button gym-button--secondary" disabled={!report || status !== 'ready' || !!exporting || !!rangeError} onClick={() => download('csv')}>
            {exporting === 'csv' ? 'Preparando CSV…' : 'Descargar CSV'}
          </button>
          <button type="button" className="gym-button gym-button--secondary" disabled={!report || status !== 'ready' || !!exporting || !!rangeError} onClick={() => download('xlsx')}>
            {exporting === 'xlsx' ? 'Preparando XLSX…' : 'Descargar XLSX'}
          </button>
        </div>
      </header>

      <div className="gym-operations__filters" aria-label="Período del reporte">
        <div className="gym-field">
          <label className="gym-field__label" htmlFor="operations-from">Desde mes</label>
          <input id="operations-from" className="gym-input" type="month" value={filters.from} onChange={(event) => changeFilter('from', event.target.value)} />
        </div>
        <div className="gym-field">
          <label className="gym-field__label" htmlFor="operations-to">Hasta mes</label>
          <input id="operations-to" className="gym-input" type="month" value={filters.to} onChange={(event) => changeFilter('to', event.target.value)} />
        </div>
        {report && <p className="gym-operations__range">Período informado por el servidor: {monthLabel(report.from)} a {monthLabel(report.to)}</p>}
      </div>

      {rangeError && <p className="gym-form__error" role="alert">{rangeError}</p>}
      {exportError && <p className="gym-form__error" role="alert">{exportError}</p>}
      {status === 'loading' && <p className="gym-operations__state" role="status">Cargando reportes…</p>}
      {status === 'error' && (
        <div className="gym-state gym-state--error" role="alert">
          <p className="gym-state__text">{message}</p>
          <button type="button" className="gym-button gym-button--secondary" onClick={() => load(lastParams.current)}>Reintentar</button>
        </div>
      )}

      {status === 'ready' && report && (
        <>
          <div className="gym-operations__metrics" aria-label="Indicadores del último mes">
            {metricCard('Cobrado', metricValue(latest, 'collectedCents'), latest ? monthLabel(latest.month) : '', 'featured')}
            {metricCard('Socios al día', metricValue(latest, 'activeMembers'), latest ? monthLabel(latest.month) : '')}
            {metricCard('Altas', metricValue(latest, 'newMembers'), latest ? monthLabel(latest.month) : '')}
            {metricCard('Bajas', metricValue(latest, 'deactivations'), latest && latest.deactivations == null ? 'Cobertura no disponible' : monthLabel(latest && latest.month))}
            {metricCard('Morosos', metricValue(latest, 'delinquentMembers'), latest ? monthLabel(latest.month) : '', 'danger')}
            {metricCard('Saldo vencido', metricValue(latest, 'overdueBalanceCents'), latest ? monthLabel(latest.month) : '', 'danger')}
          </div>

          <section className="gym-operations__section" aria-labelledby="operations-monthly-title">
            <header className="gym-operations__section-heading">
              <div><h2 id="operations-monthly-title">Evolución mensual</h2><p>Los importes son pagos confirmados y las cifras reflejan cada cierre.</p></div>
            </header>
            <div className="gym-operations__table-wrap">
              <table className="gym-table gym-operations__monthly" aria-label="Resumen mensual">
                <thead><tr><th scope="col">Mes</th><th scope="col">Cobrado</th><th scope="col">Al día</th><th scope="col">Altas</th><th scope="col">Bajas</th><th scope="col">Morosos</th><th scope="col">Saldo vencido</th></tr></thead>
                <tbody>{monthly.map((row) => {
                  const amount = Number(row.collectedCents) || 0;
                  const width = Math.round((amount / maxCollections) * 100);
                  return (
                    <tr key={row.month}>
                      <th scope="row">
                        <span>{monthLabel(row.month)}</span>
                        {row.historyPartial && <span className="gym-operations__partial">Cobertura parcial</span>}
                      </th>
                      <td data-label="Cobrado">
                        <span>{money(amount)}</span>
                        <span className="gym-operations__bar-track" aria-hidden="true"><span className="gym-operations__bar" style={{ width: `${width}%` }} /></span>
                        {amount === 0 && <span className="gym-operations__no-data">Sin datos</span>}
                      </td>
                      <td data-label="Al día">{Number(row.activeMembers || 0).toLocaleString('es-AR')}</td>
                      <td data-label="Altas">{Number(row.newMembers || 0).toLocaleString('es-AR')}</td>
                      <td data-label="Bajas">{row.deactivations == null ? <span className="gym-muted-text">Sin historial</span> : Number(row.deactivations).toLocaleString('es-AR')}</td>
                      <td data-label="Morosos">{Number(row.delinquentMembers || 0).toLocaleString('es-AR')}</td>
                      <td data-label="Saldo vencido">{money(row.overdueBalanceCents)}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            <p className="gym-operations__coverage">El historial de bajas está disponible desde {dateLabel(report.historyAvailableFrom)}. Los meses anteriores se muestran como “sin historial”.</p>
          </section>

          <section className="gym-operations__section" aria-labelledby="operations-attendance-title">
            <header className="gym-operations__section-heading">
              <div><h2 id="operations-attendance-title">Asistencia</h2><p>{Number(attendance.totalCheckins || 0).toLocaleString('es-AR')} ingresos en el período.</p></div>
            </header>
            <div className="gym-operations__attendance-grid">
              <div className="gym-operations__subsection">
                <h3>Por día de la semana</h3>
                <table className="gym-operations__weekday" aria-label="Asistencia por día de la semana">
                  <thead><tr>{WEEKDAYS.map((day) => <th scope="col" key={day}>{day}</th>)}</tr></thead>
                  <tbody><tr>{WEEKDAYS.map((day, index) => {
                    const item = (attendance.byWeekday || []).find((entry) => Number(entry.weekday) === index + 1);
                    const count = Number((item && item.checkins) || 0);
                    const barHeight = count === 0
                      ? 0
                      : Math.max(1, Math.round((count / maxWeekdayCheckins) * 100));
                    return <td key={day}><span className="gym-operations__day-bar" style={{ height: `${barHeight}%` }} /><strong>{count}</strong></td>;
                  })}</tr></tbody>
                  <tfoot><tr>{WEEKDAYS.map((day) => <th key={day}>{day.slice(0, 3)}</th>)}</tr></tfoot>
                </table>
              </div>
              <div className="gym-operations__subsection">
                <h3>Por hora</h3>
                <table className="gym-operations__hours" aria-label="Asistencia por horario">
                  <caption className="gym-sr-only">Check-ins para cada hora del día</caption>
                  <tbody>{Array.from({ length: 4 }, (_, row) => <tr key={row}>{Array.from({ length: 6 }, (_, col) => {
                    const hour = row * 6 + col;
                    const item = (attendance.byHour || []).find((entry) => Number(entry.hour) === hour);
                    const count = Number((item && item.checkins) || 0);
                    return <td key={hour} aria-label={`${String(hour).padStart(2, '0')}:00, ${count} ingresos`} title={`${String(hour).padStart(2, '0')}:00 · ${count} ingresos`}><span>{String(hour).padStart(2, '0')}:00</span><strong>{count}</strong></td>;
                  })}</tr>)}</tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="gym-operations__section" aria-labelledby="operations-ranking-title">
            <header className="gym-operations__section-heading"><div><h2 id="operations-ranking-title">Socios con más visitas</h2><p>Primeros diez del período seleccionado.</p></div></header>
            {topMembers.length === 0 ? <p className="gym-operations__empty">Todavía no hay ingresos en este período.</p> : (
              <ol className="gym-operations__ranking">{topMembers.slice(0, 10).map((member, index) => (
                <li key={member.memberUserId}><span className="gym-operations__rank">{String(index + 1).padStart(2, '0')}</span><span className="gym-operations__member-name">{member.memberName}</span><strong>{Number(member.checkins || 0).toLocaleString('es-AR')} visitas</strong></li>
              ))}</ol>
            )}
          </section>
        </>
      )}
    </section>
  );
}
